/**
 * surveyの実行状態を管理するComposable (複数ウィンドウ / タブ に対応)
 */
import { computed, onMounted, onUnmounted, ref } from 'vue';
import api from '@/api';
import type {
  DeepModeStats,
  SurveyExecution,
  SurveyExecutionStatus,
} from '@/types';
import {
  DEEP_MODE_MAX_CONCURRENCY,
  MAX_POLLING_EMPTY_RESULT_COUNT,
  SURVEY_EXECUTION_STATUS_POLLING_INITIAL_DURATION_MS,
  SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_INITIAL_MS,
  SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_MS,
} from '../survey/constants';

/**
 * global states
 */

// 実際（サーバーサイド）の実行状態（ポーリングで逐次取得）
const globalSurveyStatuses = ref<Record<string, SurveyExecution>>({});
const globalDeepModeStats = ref<DeepModeStats>({
  running: 0,
  maxConcurrency: DEEP_MODE_MAX_CONCURRENCY,
  canStartNew: false,
});

// タブ間通信
const CHANNEL_NAME = 'survey_execution_status';
const broadcastChannel = new BroadcastChannel(CHANNEL_NAME);

// マスタータブ管理
let pollingTimerId: number | null = null;
let subscriberCount = 0;
let isMasterTab = false;
let masterCheckTimeout: number | null = null;
let pollingEmptyResultCount = 0; // ポーリングで実行中状態が取得できなかった回数
let pollingStartTime: number | null = null; // ポーリング開始時刻

export function useSurveyExecutionStatus({
  enablePolling = true,
}: { enablePolling?: boolean } = {}) {
  /**
   * Local states
   */

  const isLoading = ref(false);
  const lastFetchTime = ref(0);

  const canStartNewDeepMode = computed(
    () => globalDeepModeStats.value.canStartNew,
  );

  /**
   * Handlers
   */

  // 最新のサーバーの実行状態を取得
  const refreshStatus = () => {
    if (isMasterTab) {
      handleMasterRequests('REQUEST_REFRESH');
    } else {
      broadcastChannel.postMessage({
        type: 'REQUEST_REFRESH',
        timestamp: Date.now(),
      });
    }
  };

  const requestPollingRestart = () => {
    if (isMasterTab) {
      handleMasterRequests('REQUEST_START_POLLING');
    } else {
      broadcastChannel.postMessage({
        type: 'REQUEST_START_POLLING',
        timestamp: Date.now(),
      });
    }
  };

  // 現在のポーリング間隔を決定する関数
  const getCurrentPollingInterval = (): number => {
    if (!pollingStartTime) {
      return SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_MS;
    }

    const elapsedTime = Date.now() - pollingStartTime;
    if (elapsedTime < SURVEY_EXECUTION_STATUS_POLLING_INITIAL_DURATION_MS) {
      return SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_INITIAL_MS;
    }

    return SURVEY_EXECUTION_STATUS_POLLING_INTERVAL_MS;
  };

  const shouldStopPolling = (): boolean => {
    const anyServer = Object.values(globalSurveyStatuses.value).some(
      s => s.inProgress,
    );
    const reachedMaxPollingEmptyResultCount =
      pollingEmptyResultCount >= MAX_POLLING_EMPTY_RESULT_COUNT;
    return !anyServer && reachedMaxPollingEmptyResultCount;
  };

  /**
   * surveyの実行状態を取得する関数
   * @param isPollingImmediateExecution - ポーリング開始時の即時実行かどうか
   */
  const fetchExecutionStatus = async (isPollingImmediateExecution = false) => {
    if (!isMasterTab) return;

    try {
      isLoading.value = true;
      const response: SurveyExecutionStatus =
        await api.fetchSurveyExecutionStatus();

      // グローバル状態更新
      globalSurveyStatuses.value = response.surveys;
      globalDeepModeStats.value = response.deepModeStats;
      lastFetchTime.value = Date.now();

      // 実行中状態のカウント処理（ポーリング開始時の即時実行でない場合のみ）
      if (!isPollingImmediateExecution) {
        const hasInProgressData = Object.values(response.surveys).some(
          s => s.inProgress,
        );
        if (hasInProgressData) {
          pollingEmptyResultCount = 0;
        } else {
          pollingEmptyResultCount++;
        }
      }

      // 全タブに状態配信
      broadcastChannel.postMessage({
        type: 'STATUS_UPDATE',
        data: {
          surveys: response.surveys,
          deepModeStats: response.deepModeStats,
        },
        timestamp: Date.now(),
      });

      // 実行中ゼロならポーリング停止
      if (enablePolling && shouldStopPolling()) {
        stopPolling();
      }
    } catch (_error) {
      // エラー時は楽観的に動作継続
      globalDeepModeStats.value.canStartNew = true;
    } finally {
      isLoading.value = false;
    }
  };

  const startPolling = () => {
    if (!isMasterTab || pollingTimerId) return;

    pollingStartTime = Date.now();
    fetchExecutionStatus(true); // 即座に1回実行

    // 動的間隔でポーリングを開始
    const scheduleNextPoll = () => {
      const interval = getCurrentPollingInterval();
      pollingTimerId = window.setTimeout(() => {
        if (pollingTimerId) {
          fetchExecutionStatus();
          scheduleNextPoll(); // 次のポーリングをスケジュール
        }
      }, interval);
    };

    scheduleNextPoll();
  };

  const stopPolling = () => {
    if (!isMasterTab || !pollingTimerId) return;

    // 初期化
    clearTimeout(pollingTimerId);
    pollingTimerId = null;
    pollingEmptyResultCount = 0;
    pollingStartTime = null;
  };

  const becomeMaster = () => {
    if (isMasterTab) return;

    isMasterTab = true;
    if (enablePolling) {
      startPolling();
    }
  };

  const becomeFollower = () => {
    if (!isMasterTab) return;

    isMasterTab = false;
    stopPolling();
  };

  // マスタータブ専用ロジック
  const handleMasterRequests = (type: string) => {
    if (!isMasterTab) return;

    switch (type) {
      case 'REQUEST_REFRESH':
        fetchExecutionStatus();
        break;

      case 'REQUEST_START_POLLING':
        if (pollingTimerId) {
          // NOTE: ポーリング中に別スレッドなど追加でsurveyが実行した場合は、
          // そこからさらに1分間は短い間隔でポーリングしたいので、ポーリング開始時刻を初期化
          pollingStartTime = Date.now();
        } else {
          startPolling();
        }
        break;
    }
  };

  // BroadcastChannel ハンドラー
  const handleBroadcastMessage = (event: MessageEvent) => {
    const { type, data, timestamp } = event.data;

    switch (type) {
      case 'STATUS_UPDATE':
        // 全タブが受信：マスタータブからの状態更新
        if (timestamp > lastFetchTime.value) {
          globalSurveyStatuses.value = data.surveys;
          globalDeepModeStats.value = data.deepModeStats;
          lastFetchTime.value = timestamp;
        }
        break;

      // マスタータブ専用要求
      case 'REQUEST_REFRESH':
      case 'REQUEST_START_POLLING':
        handleMasterRequests(type);
        break;

      // マスタータブ管理
      case 'MASTER_CHECK':
        if (isMasterTab) {
          broadcastChannel.postMessage({
            type: 'MASTER_EXISTS',
            timestamp: Date.now(),
          });
        }
        break;

      case 'MASTER_EXISTS':
        if (masterCheckTimeout) {
          clearTimeout(masterCheckTimeout);
          masterCheckTimeout = null;
        }
        becomeFollower();
        break;

      case 'MASTER_LEAVING':
        if (!isMasterTab) {
          setTimeout(
            () => {
              if (!isMasterTab && subscriberCount > 0) {
                becomeMaster();
              }
            },
            Math.random() * 1000 + 500,
          );
        }
        break;
    }
  };

  const determineMasterTab = () => {
    // 既存のマスターをチェック
    broadcastChannel.postMessage({
      type: 'MASTER_CHECK',
      timestamp: Date.now(),
    });

    // 300ms以内に応答がなければマスターになる
    masterCheckTimeout = window.setTimeout(() => {
      if (subscriberCount > 0) {
        becomeMaster();
      }
    }, 300);
  };

  // タブが閉じられる際の処理
  const handleBeforeUnload = () => {
    if (isMasterTab) {
      broadcastChannel.postMessage({
        type: 'MASTER_LEAVING',
        timestamp: Date.now(),
      });
    }
  };

  /**
   * タブがアクティブになった時、マスタータブが存在するかチェック
   */
  const handleVisibilityChange = () => {
    if (!document.hidden && subscriberCount > 0) {
      determineMasterTab();
    }
  };

  const subscribe = () => {
    subscriberCount++;

    if (subscriberCount === 1) {
      broadcastChannel.addEventListener('message', handleBroadcastMessage);
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      determineMasterTab();
    }
  };

  const unsubscribe = () => {
    subscriberCount--;

    if (subscriberCount <= 0) {
      subscriberCount = 0;

      // マスタータブが終了する場合、他のタブに通知
      if (isMasterTab) {
        broadcastChannel.postMessage({
          type: 'MASTER_LEAVING',
          timestamp: Date.now(),
        });
      }

      stopPolling();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      broadcastChannel.removeEventListener('message', handleBroadcastMessage);

      if (masterCheckTimeout) {
        clearTimeout(masterCheckTimeout);
        masterCheckTimeout = null;
      }

      isMasterTab = false;
    }
  };

  const isSurveyInProgress = (surveyId: string): boolean => {
    return globalSurveyStatuses.value[surveyId]?.inProgress ?? false;
  };

  /**
   * Lifecycle Hooks
   */

  onMounted(() => {
    if (enablePolling) {
      subscribe();
    }
  });

  onUnmounted(() => {
    if (enablePolling) {
      unsubscribe();
    }
  });

  return {
    // ディープリサーチ関連
    canStartNewDeepMode,

    // 個別survey状態チェック（survey.tsのgettersと同等）
    isSurveyInProgress,

    // 操作（内部でマスタータブ判定しない）
    refreshStatus,
    requestPollingRestart,
  };
}
